/*
 * Iter 31.309 Layer 1 — LinkedIn Contacts tile.
 *
 * Import a Connections.csv (LinkedIn's own export), export to Drive
 * as CSV or vCard. First-class home for the passive network so it
 * doesn't dilute the canonical 277-operative roster.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, getToken } from "@/lib/api";
import { Loader2, Upload, Download, ExternalLink, AlertTriangle, UsersRound } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LinkedInContactsPanel() {
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(null);        // "import" | "drive-csv" | "drive-vcf"
    const [driveResult, setDriveResult] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const fileRef = useRef(null);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get("/linkedin-contacts", { params: { limit: 1 } });
            setTotal(data?.total || 0);
            setErr(null);
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const importCsv = useCallback(async (file) => {
        if (!file) return;
        setBusy("import"); setImportResult(null); setErr(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/linkedin-contacts/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImportResult(data);
            reload();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally { setBusy(null); }
    }, [reload]);

    const downloadLocal = useCallback((fmt) => {
        const token = getToken();
        (async () => {
            try {
                const resp = await fetch(
                    `${API}/api/linkedin-contacts/export?fmt=${fmt}`,
                    { headers: { Authorization: `Bearer ${token}` } },
                );
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const blob = await resp.blob();
                const cd = resp.headers.get("content-disposition") || "";
                const m = cd.match(/filename="([^"]+)"/);
                const name = m ? m[1] : `linkedin_contacts.${fmt === "vcard" ? "vcf" : "csv"}`;
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = name;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } catch (e) { window.alert(`Export failed: ${e.message}`); }
        })();
    }, []);

    const exportToDrive = useCallback(async (fmt) => {
        setBusy(fmt === "vcard" ? "drive-vcf" : "drive-csv");
        setDriveResult(null); setErr(null);
        try {
            const { data } = await api.post(`/admin/linkedin-contacts/export-to-drive?fmt=${fmt}`);
            setDriveResult(data);
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally { setBusy(null); }
    }, []);

    return (
        <div
            className="paper-card border-2 border-ink/30 p-5 mb-6"
            data-testid="linkedin-contacts-panel"
        >
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading uppercase tracking-[0.22em] text-sm text-ink flex items-center gap-2">
                    <UsersRound className="w-4 h-4 text-stamp-red" />
                    LinkedIn Contacts
                </h2>
                <button
                    type="button"
                    onClick={reload}
                    disabled={loading}
                    data-testid="lc-refresh"
                    className="font-heading uppercase tracking-[0.16em] text-[10px] text-ink-soft hover:text-stamp-red disabled:opacity-50"
                >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "refresh"}
                </button>
            </div>

            <div className="font-body text-[11px] text-ink-soft mb-3 leading-relaxed">
                Iter 31.309 · Layer 1 — kept separate from the 277-operative roster. Import your LinkedIn <code className="font-mono">Connections.csv</code>, export to Google Drive as CSV or vCard. Data is portable and non-lossy.
            </div>

            {err ? (
                <div className="mb-3 label-typed text-[11px] leading-relaxed border-l-2 border-stamp-red pl-3 text-stamp-red flex items-start gap-1" data-testid="lc-err">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{err}</span>
                </div>
            ) : null}

            <div className="border border-ink/20 p-3 mb-3 font-body text-[11px] text-ink-soft" data-testid="lc-count">
                Holding <span className="font-mono text-stamp-red font-bold">{total}</span> contact(s).
            </div>

            {/* Import */}
            <div className="border border-ink/20 p-3 mb-3">
                <div className="label-typed text-[10px] text-ink mb-2 flex items-center gap-2">
                    <Upload className="w-3 h-3" />
                    Import LinkedIn Connections.csv
                </div>
                <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => importCsv(e.target.files?.[0])}
                    data-testid="lc-import-file"
                    className="font-mono text-[11px] text-ink"
                    disabled={busy === "import"}
                />
                {busy === "import" ? (
                    <div className="mt-2 text-[11px] text-ink-soft flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> importing…
                    </div>
                ) : null}
                {importResult ? (
                    <div className="mt-2 font-body text-[11px] text-emerald-800" data-testid="lc-import-result">
                        Import complete — <span className="font-mono">{importResult.inserted}</span> new,{" "}
                        <span className="font-mono">{importResult.updated}</span> updated,{" "}
                        <span className="font-mono">{importResult.skipped}</span> skipped.
                    </div>
                ) : null}
            </div>

            {/* Export */}
            <div className="border border-ink/20 p-3">
                <div className="label-typed text-[10px] text-ink mb-2 flex items-center gap-2">
                    <Download className="w-3 h-3" />
                    Export
                </div>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                    <button type="button" onClick={() => downloadLocal("csv")} data-testid="lc-dl-csv"
                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red">
                        Download CSV
                    </button>
                    <button type="button" onClick={() => downloadLocal("vcard")} data-testid="lc-dl-vcf"
                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red">
                        Download vCard
                    </button>
                    <span className="text-ink-faint text-[10px]">|</span>
                    <button type="button" onClick={() => exportToDrive("csv")} disabled={!!busy} data-testid="lc-drive-csv"
                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red/10 disabled:opacity-40 inline-flex items-center gap-1.5">
                        {busy === "drive-csv" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        Save CSV to Drive
                    </button>
                    <button type="button" onClick={() => exportToDrive("vcard")} disabled={!!busy} data-testid="lc-drive-vcf"
                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red/10 disabled:opacity-40 inline-flex items-center gap-1.5">
                        {busy === "drive-vcf" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        Save vCard to Drive
                    </button>
                </div>
                {driveResult ? (
                    <div className="mt-2 font-body text-[11px] text-emerald-800" data-testid="lc-drive-result">
                        Saved <span className="font-mono">{driveResult.filename}</span> ·{" "}
                        <span className="font-mono text-stamp-red font-bold">{driveResult.count}</span> contact(s)
                        {driveResult.drive_link ? (
                            <a href={driveResult.drive_link} target="_blank" rel="noopener noreferrer"
                               className="ml-2 text-stamp-red hover:underline inline-flex items-center gap-1">
                                open in Drive <ExternalLink className="w-3 h-3" />
                            </a>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
}
